import React, { useEffect, useState } from 'react';
import { Form, Select, InputNumber, message, Tag, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import StandardDrawer from '../../components/common/StandardDrawer';
import * as personnelService from '../../services/personnel/personnelService';

// 人员等级选项
const LEVEL_OPTIONS = [
  { value: 'L1', label: 'L1' },
  { value: 'L2', label: 'L2' },
  { value: 'L3', label: 'L3' },
];

// 获取可执行的任务等级
const getExecutableTaskLevels = (levelName) => {
  switch (levelName) {
    case 'L1':
      return ['L1'];
    case 'L2':
      return ['L1', 'L2'];
    case 'L3':
      return ['L1', 'L2', 'L3'];
    default:
      return [];
  }
};

/**
 * LevelRuleDrawer - Create/Edit personnel level rules and merge order strategy
 */
const LevelRuleDrawer = ({ visible, level, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const isEdit = !!level;
  const [selectedLevel, setSelectedLevel] = useState(null);

  useEffect(() => {
    if (visible) {
      if (level) {
        form.setFieldsValue({
          levelName: level.levelName,
          maxOrdersPerTrip: level.maxOrdersPerTrip,
          wetherDefault: level.wetherDefault || false,
        });
        setSelectedLevel(level.levelName);
      } else {
        form.resetFields();
        setSelectedLevel(null);
      }
    } else {
      // 关闭时重置状态，避免下次打开时残留旧数据
      setSelectedLevel(null);
    }
  }, [visible, level, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const requestData = {
        ...values,
      };

      let response;
      if (isEdit) {
        requestData.id = level.id;
        response = await personnelService.updatePersonnelLevel(requestData);
      } else {
        response = await personnelService.createPersonnelLevel(requestData);
      }

      if (response.success) {
        message.success(
          isEdit ? t('personnel.updateSuccess') : t('personnel.createSuccess')
        );
        onSuccess();
      } else {
        message.error(
          response.errorMessage ||
            (isEdit ? t('personnel.updateFailed') : t('personnel.createFailed'))
        );
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLevelChange = (value) => {
    setSelectedLevel(value);
  };

  return (
    <StandardDrawer
      visible={visible}
      onClose={onClose}
      onOk={handleSubmit}
      title={isEdit ? `${t('personnel.editRuleTitle')} - ${level?.levelName}` : t('personnel.addLevelTitle')}
      confirmLoading={loading}
      width={480}
      maskClosable={false}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ padding: '24px 0' }}
      >
        <div style={{ marginBottom: 8, fontWeight: 'bold', fontSize: 14 }}>
          {t('personnel.capacitySection')}
        </div>

        <Form.Item
          name="levelName"
          label={t('personnel.levelName')}
          rules={[
            { required: true, message: t('personnel.levelNameRequired') },
          ]}
        >
          <Select
            placeholder={t('personnel.levelNamePlaceholder')}
            options={LEVEL_OPTIONS}
            onChange={handleLevelChange}
          />
        </Form.Item>

        <Form.Item
          name="maxOrdersPerTrip"
          label={t('personnel.maxOrdersPerTrip')}
          rules={[
            { required: true, message: t('personnel.maxOrdersRequired') },
          ]}
        >
          <InputNumber
            min={1}
            max={999}
            style={{ width: '100%' }}
            placeholder={t('common.pleaseInput')}
            addonAfter={t('personnel.ordersPerTrip')}
          />
        </Form.Item>

        <Form.Item
          name="wetherDefault"
          label={t('personnel.wetherDefault')}
          valuePropName="checked"
          extra={<span style={{ color: '#999', fontSize: 12 }}>{t('personnel.wetherDefaultHint')}</span>}
        >
          <Switch />
        </Form.Item>

        {selectedLevel && (
          <Form.Item label={t('personnel.executableTaskLevels')}>
            <div key={selectedLevel}>
              {getExecutableTaskLevels(selectedLevel).map((lvl) => (
                <Tag key={lvl} color="blue" style={{ marginRight: 8 }}>
                  {lvl}
                </Tag>
              ))}
            </div>
            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
              {t('personnel.executableTaskLevelsDesc')}
            </div>
          </Form.Item>
        )}
      </Form>
    </StandardDrawer>
  );
};

export default LevelRuleDrawer;
